import moment from 'moment';

export interface ArrivalBucket {
	/** Human-readable bucket-start label (time-of-day within a day, date+time across days). */
	label: string;
	/** Number of entries (scans) that fell in this interval. */
	count: number;
}

// "Nice" bucket widths in minutes — the smallest one ≥ the raw target width is
// chosen so the axis lands on readable boundaries (quarter-hours, hours, days).
const NICE_STEPS_MIN = [15, 30, 60, 120, 180, 360, 720, 1440] as const;
const TARGET_BUCKETS = 16;
const MS_PER_MIN = 60_000;

/**
 * Buckets per-ticket scan timestamps into a contiguous arrivals-over-time series
 * for the Show Day chart. Picks a bucket width that yields roughly
 * TARGET_BUCKETS bars across the scanning window, fills empty intervals with 0
 * (so lulls in the door flow are visible), and labels each bucket by its start.
 *
 * Returns [] when there are no valid timestamps (caller shows the no-data state).
 * Pure + deterministic — no reliance on the current time.
 */
export function bucketArrivals(timestamps: Array<string | null | undefined>): ArrivalBucket[] {
	const times = timestamps
		.map((t) => (t ? new Date(t).getTime() : NaN))
		.filter((ms) => !Number.isNaN(ms))
		.sort((a, b) => a - b);
	if (times.length === 0) return [];

	const min = times[0];
	const max = times[times.length - 1];
	const spanMin = Math.max(1, (max - min) / MS_PER_MIN);
	const rawStep = spanMin / TARGET_BUCKETS;
	const stepMin = NICE_STEPS_MIN.find((s) => s >= rawStep) ?? NICE_STEPS_MIN[NICE_STEPS_MIN.length - 1];
	const stepMs = stepMin * MS_PER_MIN;

	// Snap the first bucket down to a step boundary so labels read cleanly.
	const start = Math.floor(min / stepMs) * stepMs;
	const counts = new Map<number, number>();
	for (const ms of times) {
		const bucketStart = Math.floor(ms / stepMs) * stepMs;
		counts.set(bucketStart, (counts.get(bucketStart) ?? 0) + 1);
	}

	// Cross-day windows need the date on each label; single-day windows don't.
	const multiDay = max - start >= 24 * 60 * MS_PER_MIN;
	const fmt = multiDay ? 'MMM D, h:mm A' : 'h:mm A';

	const buckets: ArrivalBucket[] = [];
	for (let bucket = start; bucket <= max; bucket += stepMs) {
		buckets.push({ label: moment(bucket).format(fmt), count: counts.get(bucket) ?? 0 });
	}
	return buckets;
}
